import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { TranscriptBlocks } from './components/blocks';
import { TranscriptInconsistencies } from './components/inconsistencies';
import { LiveControls } from './components/live-controls';
import { PlaybackControls } from './components/playback-controls';
import { SpeakersPanel } from './components/speakers-panel';
import { SummaryModal } from './components/summary-modal';
import { TranscriptHeader } from './components/transcript-header';
import { useScrollToBottom } from './hooks/useScroll';
import { TranscriptStatus } from '../../../../../../../../../../backend/src/case/transcription/transcript-enums';
import { PageName } from '../../../../../../../../analytics/events';
import { trackPageView } from '../../../../../../../../analytics/Mixpanel';
import { usePlaybackController } from '../../../../../../../../screens/Chronos/transcribe/components/PlaybackController';
import { useSpeakerManagement } from '../../../../../../../../screens/Chronos/transcribe/hooks/useSpeakerManagement';
import { useTranscription } from '../../../../../../../../screens/Chronos/transcribe/hooks/useTranscription';
import { useTranscriptInconsistenciesStore } from '../../../../../../../../screens/Chronos/transcribe/stores/transcriptInconsistenciesStore';
import { LiveTranscriptBlock } from '../../../../../../../../screens/Chronos/transcribe/types/transcriptTypes';
import { Icon } from '../../../../../../../components/icon';
import { APIBaseChronos } from '@/api/hosts';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetTranscript } from '@/api/queries/useGetTranscripts';
import { TranscriptInconsistency } from '@/api/types/backend-dto-types';
import { Breadcrumb } from '@/new/components/breadcrumb';

export function Transcript() {
  const { matterId, subsetId, transcriptId } = useParams();
  if (!matterId || !subsetId || !transcriptId) return;

  // Track page view
  useEffect(() => {
    trackPageView(PageName.Transcription);
  }, []);

  const { data: matter } = useGetMatterV2(matterId);
  const { data: caseData } = useGetCase(subsetId);
  const { data: transcript } = useGetTranscript(transcriptId, subsetId);

  // document.title = transcript ? `${transcript.name} • Realtime • wexler.ai` : 'Realtime • wexler.ai';

  const [storeUpdateTrigger, setStoreUpdateTrigger] = useState(0);

  useEffect(() => {
    const unsubscribe = useTranscriptInconsistenciesStore.subscribe(() => {
      setStoreUpdateTrigger((prev) => prev + 1);
    });
    return unsubscribe;
  }, []);

  const isPlaybackMode = transcript?.status === TranscriptStatus.Completed;

  const { initializeInconsistencies, getInconsistencies } = useTranscriptInconsistenciesStore();

  // Initialize inconsistencies store with saved transcript data
  useEffect(() => {
    if (transcript?.id && transcript.inconsistencies) {
      initializeInconsistencies(transcript.id, transcript.inconsistencies);
    }
  }, [transcript?.id, transcript?.inconsistencies?.length, initializeInconsistencies]);

  const {
    isRecording,
    isPaused,
    isConnected,
    isCompleting,
    blocks,
    logicalSpeakers,
    internalSpeakers,
    startRecording,
    pauseRecording,
    completeRecording,
    mapInternalSpeaker,
    acceptDiarizationCorrection,
    rejectDiarizationCorrection,
    updateBlock,
  } = useTranscription(subsetId, transcriptId, transcript);

  const sortedInconsistencies = useMemo(() => {
    const storeInconsistencies = transcriptId ? getInconsistencies(transcriptId) : [];

    if (isPlaybackMode && blocks && storeInconsistencies.length > 0) {
      return [...storeInconsistencies].sort((a, b) => {
        const getEarliestTimestamp = (inconsistency: TranscriptInconsistency) => {
          const referencedBlocks = inconsistency.transcriptSubfact?.referencedBlocks || [];
          if (referencedBlocks.length === 0) return Infinity;

          const timestamps = referencedBlocks
            .map((refBlock: { id: string }) => {
              const transcriptBlock = blocks.find((block) => block.id === refBlock.id);
              return transcriptBlock ? Number(transcriptBlock.timestamp) : Infinity;
            })
            .filter((timestamp: number) => timestamp !== Infinity);

          return timestamps.length > 0 ? Math.min(...timestamps) : Infinity;
        };

        return getEarliestTimestamp(a) - getEarliestTimestamp(b);
      });
    }

    return storeInconsistencies;
  }, [storeUpdateTrigger, isPlaybackMode, blocks]);

  const audioData =
    subsetId && transcriptId
      ? { url: `${APIBaseChronos}/api/case/${subsetId}/transcripts/${transcriptId}/audio/full` }
      : null;

  const playbackController = usePlaybackController({
    audioUrl: audioData?.url,
  });

  const { transcriptContainerRef, isUserScrolling, handleScroll, scrollToBottom, scrollToTranscriptBlock } =
    useScrollToBottom(isPlaybackMode ? [] : blocks);

  const handleBlockClick = (block: LiveTranscriptBlock) => {
    if (isPlaybackMode && playbackController) {
      playbackController.seekTo(Number(block.timestamp));
    } else {
      scrollToTranscriptBlock(block.id);
    }
  };

  const handleScrollToBlockFromInconsistency = (blockId: string) => {
    console.log(isPlaybackMode, blocks);
    if (isPlaybackMode && blocks) {
      const block = blocks.find((b) => b.id === blockId);
      if (block) {
        // Navigate to that transcript block & set the appropriate time on the playback
        const blockElement = document.getElementById(`block-${blockId}`);
        console.log(blockElement);

        if (blockElement) {
          blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        if (playbackController) {
          playbackController.seekTo(Number(block.timestamp));
        }
      }
    } else {
      scrollToTranscriptBlock(blockId);
    }
  };

  // Get internal speaker ids for blocks with only internal assignment
  const blockInternalSpeakerIds = blocks
    .filter((block) => !block.internalSpeaker?.speaker)
    .map((block) => block.internalSpeakerId);
  const displayInternalSpeakers = internalSpeakers.filter((i) => blockInternalSpeakerIds.includes(i.id));

  const { editingSpeaker, editingName, startEditingSpeaker, cancelEditingSpeaker, saveSpeakerMapping, setEditingName } =
    useSpeakerManagement(
      logicalSpeakers,
      displayInternalSpeakers,
      mapInternalSpeaker || (() => {}),
      matterId,
      transcriptId || null,
    );

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={
          matter && caseData
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false },
                { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: false },
                {
                  href: `/matters/${matterId}/subsets/${subsetId}`,
                  label: caseData.name,
                  active: false,
                  iconClass: 'lucide--group',
                },
                {
                  href: `/matters/${caseData.id}/subsets/${subsetId}/realtime`,
                  label: 'Realtime',
                  active: false,
                },
                ...(transcript ? [{ href: transcript.id, label: transcript.name, active: true }] : []),
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      {transcript ? (
        <div className="flex flex-1 flex-col overflow-y-hidden">
          <div className="flex w-full items-center justify-between border-b p-6 py-4">
            <TranscriptHeader
              transcript={transcript}
              allTranscriptsLink={`/matters/${matterId}/subsets/${subsetId}/realtime`}
            />

            {isPlaybackMode ? (
              <div className="flex gap-2">
                <PlaybackControls playbackController={playbackController} />
                {transcript.summary && <SummaryModal summary={transcript.summary} />}
              </div>
            ) : (
              <LiveControls
                isRecording={isRecording}
                isPaused={isPaused}
                isConnected={isConnected}
                caseId={subsetId}
                onStartRecording={startRecording}
                onPauseRecording={pauseRecording}
                onCompleteRecording={completeRecording}
              />
            )}
          </div>

          <div className="flex w-full flex-grow overflow-y-auto">
            <div className="relative min-w-3/5 overflow-hidden border-r">
              {/* Fixed fade overlay at the top */}
              <div className="pointer-events-none absolute top-0 right-6 left-0 z-10 h-18 bg-gradient-to-b from-white from-10% to-transparent" />

              <div className="h-full overflow-y-auto">
                {!isPlaybackMode && (
                  <SpeakersPanel
                    logicalSpeakers={logicalSpeakers}
                    internalSpeakers={displayInternalSpeakers}
                    editingSpeaker={editingSpeaker}
                    editingName={editingName}
                    onStartEditing={startEditingSpeaker}
                    onCancelEditing={cancelEditingSpeaker}
                    onSaveSpeaker={saveSpeakerMapping}
                    onEditingNameChange={setEditingName}
                    deponent={transcript.deponent}
                    otherSpeakers={transcript.otherSpeakers ?? []}
                  />
                )}

                <div ref={transcriptContainerRef}>
                  <TranscriptBlocks
                    displayBlocks={blocks}
                    caseId={subsetId}
                    transcriptId={transcriptId}
                    speakers={logicalSpeakers}
                    currentTime={playbackController?.state.currentTime ?? 0}
                    onBlockClick={handleBlockClick}
                    onUpdateBlock={updateBlock}
                    onAcceptCorrection={acceptDiarizationCorrection}
                    onRejectCorrection={rejectDiarizationCorrection}
                  />
                </div>
              </div>
            </div>

            <div className="z-10 h-full grow bg-gray-50">
              <TranscriptInconsistencies
                caseId={subsetId}
                displayInconsistencies={sortedInconsistencies}
                isPlaybackMode={isPlaybackMode}
                handleScrollToBlockFromInconsistency={handleScrollToBlockFromInconsistency}
                numberOfBlocks={blocks.length}
              />
            </div>
          </div>
        </div>
      ) : (
        // TODO: Update this loading state
        <div className="flex h-full w-full items-center justify-center">
          <Icon className="lucide--loader-circle animate-spin" />
        </div>
      )}
    </div>
  );
}
